import { useAuthContext } from "@/authentication/AuthProvider"
import { useViewerMaybe } from "@/authentication/ViewerProvider"
import { Button } from "@/components/ui/button"
import { FieldGroup } from "@/components/ui/field-group"
import { FormActions } from "@/components/ui/form-actions"
import { H1 } from "@/components/ui/headings"
import { RhfField } from "@/components/ui/rhf-field"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { rootPath } from "@/novus/novus-paths"
import { assert } from "@/util/assert"
import { useMutation } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { z, ZodType } from "zod"

type FormValues = {
  newPassword: string
  newPasswordConfirmation: string
}

export const UpdatePasswordMutation = gql(`
  mutation UpdatePassword($input: ResetPasswordAndLoginInput!) {
    resetPasswordAndLogin(input: $input) {
      sessionToken
      errors {
        fullMessages
      }
    }
  }
`)

const schema: ZodType<FormValues> = z
  .object({
    newPassword: z
      .string()
      .min(8, "A password of at least 8 characters is required"),
    newPasswordConfirmation: z
      .string()
      .min(8, "A password of at least 8 characters is required"),
  })
  .refine((values) => values.newPassword === values.newPasswordConfirmation, {
    message: "Passwords do not match",
    path: ["newPasswordConfirmation"],
  })

const UpdatePasswordForm = ({
  email,
  code,
}: {
  email: string
  code: string
}) => {
  const navigate = useNavigate()
  const { reloadViewer } = useViewerMaybe()
  const { toast } = useToast()
  const { setToken } = useAuthContext()

  const [mutate] = useMutation(UpdatePasswordMutation)

  const onSubmit = async (values: FormValues) => {
    try {
      const { data, errors } = await mutate({
        variables: {
          input: {
            password: values.newPassword,
            email,
            resetToken: code,
          },
        },
      })
      if (errors) {
        throw new Error("Error sending password reset token")
      }

      assert(data?.resetPasswordAndLogin?.sessionToken)
      setToken(data.resetPasswordAndLogin.sessionToken)
      await reloadViewer()
      navigate(rootPath.pattern)
    } catch (e: any) {
      console.error(e)
      toast({
        title:
          "Your password was not updated. Make sure you entered the correct code and try again.",
        variant: "destructive",
      })
    }
  }

  const defaultValues: FormValues = {
    newPassword: "",
    newPasswordConfirmation: "",
  }

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { control, formState } = methods

  return (
    <FormProvider {...methods}>
      <div className="mb-12">
        <H1>Update Password</H1>
      </div>

      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FieldGroup>
          <RhfField
            name="newPassword"
            control={control}
            type="password"
            placeholder="New password"
            label="New Password"
            autoFocus={true}
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="newPasswordConfirmation"
            control={control}
            type="password"
            placeholder="New password confirmation"
            label="New Password Confirmation"
          />
        </FieldGroup>
        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={formState.isSubmitting}
          >
            Update Password
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  )
}

export default UpdatePasswordForm
